"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { FileDown, Zap, CalendarDays, ChevronRight, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import SidebarSectionLabel from "@/components/home/SidebarSectionLabel";

/** 컴팩트 링크 행 chrome — HelperRow(Link)·CodingExpectedRow(button) 공유(드롭다운 트리거가 일반 행과 동일하게 보이도록). */
const ROW_CLASS =
  "group flex items-center gap-3 rounded-2xl border border-border bg-card/90 px-3 py-2.5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:border-amber-700";

/**
 * 홈 대시보드 우측 — 합격 도우미 묶음 (JCG-2110).
 *
 * 기존 단일 "합격 도우미" 배너(Banner stacked)를 의미별 3개 섹션으로 분해한다:
 * - 압축 PDF: 이론·코딩 압축 PDF
 * - 압축 공부법: 이론 예상 + 코딩 예상(C/Java/Python/SQL 드롭다운)
 * - 공부계획: 이론 4주 · 코딩 6주
 * (합격률은 별도 ExamReviewCard 로 분리됨 — 여기엔 포함하지 않는다.)
 */

interface HelperItem {
  href: string;
  label: string;
  img: string;
}

const COMPRESSED_PDFS: HelperItem[] = [
  {
    href: "/summary-pdf/26-1-compressed",
    label: "이론 압축 PDF",
    img: "/summary-pdf/26-1-compressed/3d_compressed-with-djc_gamja.webp",
  },
  {
    href: "/summary-pdf/26-1-coding-compressed",
    label: "코딩 압축 PDF",
    img: "/summary-pdf/26-1-coding-compressed/coding_compressed_gamja.webp",
  },
];

const STUDY_PLANS: HelperItem[] = [
  {
    href: "/theory/4week-study-plan",
    label: "이론 4주 공부계획",
    img: "/theory/contents/4week-study-plan/detective_gamja_1.webp",
  },
  {
    href: "/coding/coding-study-plan",
    label: "코딩 6주 공부계획",
    img: "/main/dev_gamza.webp",
  },
];

const CODING_EXPECTED_LANGS = [
  { href: "/coding/coding-expected-questions", label: "C언어" },
  { href: "/coding/coding-expected-questions-java", label: "Java" },
  { href: "/coding/coding-expected-questions-python", label: "Python" },
  { href: "/coding/coding-expected-questions-sql", label: "SQL" },
];

export default function HomeHelperSections() {
  return (
    <>
      {/* 압축 PDF */}
      <HelperSection icon={FileDown} title="압축 PDF">
        {COMPRESSED_PDFS.map((it) => (
          <HelperRow key={it.href} {...it} />
        ))}
      </HelperSection>

      {/* 압축 공부법 */}
      <HelperSection icon={Zap} title="압축 공부법">
        <HelperRow
          href="/theory/theory-expected-questions"
          label="3회 이론 예상"
          img="/main/compressed_gamja.webp"
        />
        <CodingExpectedRow />
      </HelperSection>

      {/* 공부계획 */}
      <HelperSection icon={CalendarDays} title="공부계획">
        {STUDY_PLANS.map((it) => (
          <HelperRow key={it.href} {...it} />
        ))}
      </HelperSection>
    </>
  );
}

/** 우측 컬럼 섹션 — 라벨(아이콘 + uppercase muted) + 행 묶음. */
function HelperSection({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <SidebarSectionLabel icon={Icon}>{title}</SidebarSectionLabel>
      <div className="flex flex-col gap-2">{children}</div>
    </section>
  );
}

/** 감자 아이콘 + 라벨 + chevron 의 컴팩트 링크 행. */
function HelperRow({ href, label, img }: HelperItem) {
  return (
    <Link href={href} prefetch={false} className={ROW_CLASS}>
      <Image src={img} alt="" width={36} height={36} className="h-9 w-9 shrink-0 object-contain" />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{label}</span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </Link>
  );
}

/** 코딩 예상 — 언어(C/Java/Python/SQL) 선택 드롭다운 행. */
function CodingExpectedRow() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={`${ROW_CLASS} w-full text-left`}>
          <Image
            src="/coding/c-language/c-language_gamja.webp"
            alt=""
            width={36}
            height={36}
            className="h-9 w-9 shrink-0 object-contain"
          />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
            3회 코딩 예상
          </span>
          <span className="shrink-0 text-[11px] font-medium text-muted-foreground">언어 선택</span>
          <ChevronDown
            className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {CODING_EXPECTED_LANGS.map((lang) => (
          <DropdownMenuItem key={lang.href} asChild>
            <Link href={lang.href} className="cursor-pointer font-medium">
              {lang.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
